/**
 * Document model types — the single source of truth for an editable image.
 *
 * Rules that keep us "correct":
 *  - Everything here is plain, structured-clone-able data (no class instances).
 *    That makes the model serialisable, worker-transferable and diffable for free.
 *  - The model is versioned. Any shape change bumps SCHEMA_VERSION and adds a
 *    migration in serialize.ts, so old .webpaint files never break.
 *  - Layer index 0 is the BOTTOM layer (matches how users read a layer list
 *    bottom-to-top in the panel).
 */

export const SCHEMA_VERSION = 4 as const;

/** Matches CSS `mix-blend-mode` / Canvas2D `globalCompositeOperation` names 1:1. */
export const BLEND_MODES = [
  'normal',
  'multiply',
  'screen',
  'overlay',
  'darken',
  'lighten',
  'color-dodge',
  'color-burn',
  'hard-light',
  'soft-light',
  'difference',
  'exclusion',
  'hue',
  'saturation',
  'color',
  'luminosity',
] as const;

export type BlendMode = (typeof BLEND_MODES)[number];

export interface Vec2 {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type LayerType = 'image' | 'paint' | 'shape' | 'text' | 'fill' | 'adjustment' | 'group';

/**
 * A layer mask: a stencil that hides part of the layer it belongs to.
 *
 * Two rules define it, and everything else follows from them:
 *
 *  - **Coverage lives in the alpha channel.** Opaque shows the layer, transparent
 *    hides it, partial alpha fades it. That is the same thing a selection is, and
 *    it means compositing a mask is one `destination-in` draw instead of a
 *    per-pixel luminance pass. (Painting a mask therefore means painting *opaque*
 *    to reveal and erasing to conceal — not "white shows, black hides".)
 *  - **The mask is in the layer's own pixel space**, the same size as the layer's
 *    raster. Moving, cropping, rotating or flipping the layer then carries the
 *    mask along for free, because it goes through the identical pixel operation.
 *
 * Masks are live: they never touch the layer's pixels until `Apply Mask` bakes
 * them in, so discarding a mask costs nothing.
 */
export interface LayerMask {
  /** PNG data URL whose alpha channel is the coverage. */
  src: string;
  /** false = kept but not applied, so a mask can be switched off and back on. */
  enabled: boolean;
  /** true = coverage is flipped on the way to the canvas. */
  inverted: boolean;
}

/** Fields shared by every layer. Values map directly onto canvas semantics. */
export interface LayerBase {
  id: string;
  name: string;
  type: LayerType;
  visible: boolean;
  locked: boolean;
  /** 0..1 */
  opacity: number;
  blendMode: BlendMode;
  /** Top-left position in document pixels. */
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  /** Rotation in degrees, clockwise. */
  angle: number;
  /** true = the layer's pixels are clipped to the layer above it (later iteration). */
  clipping: boolean;
  /** Layer mask, or null when the layer has none. */
  mask: LayerMask | null;
}

export interface ImageLayer extends LayerBase {
  type: 'image';
  /** data: URL or blob: URL. Inline so projects stay self-contained. */
  src: string;
  naturalWidth: number;
  naturalHeight: number;
}

export interface PaintLayer extends LayerBase {
  type: 'paint';
  /** PNG data URL of the painted pixels (RGBA). */
  pixels: string;
  /**
   * Intrinsic size of the `pixels` raster, in the layer's own pixel space —
   * the same fields `ImageLayer` carries, and for the same reason: nothing
   * else may assume a paint raster is canvas-sized. New paint layers start
   * canvas-sized, but crop/rotate bake the pixels down to what survives.
   * (v3; the migration fills the historical canvas size for older files.)
   */
  naturalWidth: number;
  naturalHeight: number;
}

export type ShapeKind = 'rect' | 'ellipse' | 'line' | 'arrow' | 'polygon';

export interface ShapeLayer extends LayerBase {
  type: 'shape';
  kind: ShapeKind;
  width: number;
  height: number;
  fill: string | null;
  stroke: string | null;
  strokeWidth: number;
  /** Corner radius for rects, in document pixels. */
  rx: number;
  points?: Vec2[];
}

export interface TextLayer extends LayerBase {
  type: 'text';
  text: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  italic: boolean;
  underline: boolean;
  color: string;
  align: 'left' | 'center' | 'right';
  lineHeight: number;
  /** Wrap width in document pixels, or null for a single line. */
  maxWidth: number | null;
}

export interface FillLayer extends LayerBase {
  type: 'fill';
  color: string;
}

export type AdjustmentKind =
  | 'brightness'
  | 'contrast'
  | 'saturation'
  | 'exposure'
  | 'hue'
  | 'blur'
  | 'sharpen'
  | 'pixelate'
  | 'noise'
  | 'vignette'
  | 'grayscale'
  | 'sepia'
  | 'invert'
  | 'levels'
  | 'curves';

export interface AdjustmentLayer extends LayerBase {
  type: 'adjustment';
  kind: AdjustmentKind;
  /** Normalised -1..1 (or 0..1 for blur radius) — engine maps to its own range. */
  value: number;
}

/**
 * A group: a container that holds other layers.
 *
 * A group is an **organisational container** — a folder in the layer list, not a
 * compositing unit. Its children keep their own position, scale, rotation,
 * opacity, blend mode and mask, and are drawn exactly as they would be at the
 * top of the stack. That is what makes grouping reversible and invisible: the
 * canvas shows the same pixels before and after.
 *
 * What a group *does* own: its name, its visibility and its lock. A hidden
 * group hides its children; a locked group locks them.
 *
 * The fields a layer cannot use (position, scale, rotation, opacity, blend
 * mode, mask) are inherited from `LayerBase` because the model is one union —
 * they stay at their neutral values on a group, and the UI does not offer
 * controls for them. See docs/DESIGN-iteration-11.md §2.1 for why group
 * opacity is deferred rather than approximated.
 */
export interface GroupLayer extends LayerBase {
  type: 'group';
  /** Bottom-first, like the top-level stack. */
  children: Layer[];
}

export type Layer =
  | ImageLayer
  | PaintLayer
  | ShapeLayer
  | TextLayer
  | FillLayer
  | AdjustmentLayer
  | GroupLayer;

export interface EditorDocument {
  schemaVersion: number;
  id: string;
  name: string;
  width: number;
  height: number;
  /** CSS colour, or the literal string 'transparent'. */
  background: string;
  /** Index 0 = bottom of the stack. */
  layers: Layer[];
  createdAt: string;
  modifiedAt: string;
}

export function isTransparent(color: string): boolean {
  return color === 'transparent';
}
